/**
 * CardImagePage — 카드 원본 이미지 (화면정의서 SC-008 · FN-020).
 *
 * 진입: SC-007 카드 상세에서 카드 이미지 탭 → 이탈: 뒤로 → SC-007.
 *
 * 명세가 이 화면에서 보여주라는 것은 **카드에 인쇄된 것들**이다 —
 * HP·능력·기술·약점·번호·일러스트레이터. 그건 전부 원본 이미지 안에 있는
 * 정보라, 이 화면의 일은 "이미지를 크고 선명하게, 확대해서" 보여주는 것 하나다.
 *
 * ⚠ design 원본은 더미(colorKey 그라디언트)를 그렸지만 dev 에는 실 이미지가 있다 —
 *   `largeImagePathway`(원본) 를 먼저 쓰고 없으면 목록용(`imagePathway`)으로 내려간다.
 *   ⚠ 이미지가 붙은 카드는 카탈로그 일부뿐이라 **없는 상태가 흔하다**(카드 상세와 같은
 *     사정). 그때는 확대할 것이 없으므로 자리만 비우고 아래 캡션이 아는 값을 말한다.
 *
 * ⚠ HP 는 적지 않는다 — design 은 더미의 `hp` 를 칩으로 냈지만 `CardDetail` 에 그
 *   필드가 없다. 없는 값을 만들지 않는다는 이 화면의 규칙이 그대로 적용된다.
 */
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Text } from "@astryxdesign/core/Text";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import { Token } from "@astryxdesign/core/Token";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import ZoomableImage from "../../components/primitives/ZoomableImage";
import { cardImageUrl } from "../../api/card";
import { useCardDetailQuery } from "../../hooks/useCardQueries";
import styles from "./CardImagePage.module.css";

/**
 * @param cardSerialNo ActivityHost 전용 — activity 안에서는 라우트 매치가 없어 useParams
 *   가 비므로 resolver 가 URL 에서 파싱해 넘긴다. 라우트 렌더에서는 생략.
 */
export default function CardImagePage({
  cardSerialNo: serialNoProp,
}: { cardSerialNo?: number } = {}) {
  const { t } = useTranslation("card");
  const { cardSerialNo: paramSerialNo } = useParams<{ cardSerialNo: string }>();
  const raw = serialNoProp !== undefined ? String(serialNoProp) : paramSerialNo;
  const cardSerialNo = raw && /^\d+$/.test(raw) ? Number(raw) : null;
  const detail = useCardDetailQuery(cardSerialNo);
  const card = detail.data;

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.stage}>
          <Skeleton width="100%" height="100%" />
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className={styles.page}>
        <Text color="secondary" justify="center">{t("detail.notFound")}</Text>
      </div>
    );
  }

  const imageUrl = cardImageUrl(card.largeImagePathway ?? card.imagePathway);

  return (
    <div className={styles.page}>
      <div className={styles.stage}>
        <ZoomableImage src={imageUrl} alt={card.name} fit="contain">
          {imageUrl ? undefined : <span className={styles.placeholder} aria-hidden="true" />}
        </ZoomableImage>
      </div>

      {/* 확대해도 읽을 게 없는 동안, 아는 값만이라도 적어 둔다 */}
      <VStack gap={2} className={styles.caption}>
        <Text weight="semibold">{card.name}</Text>
        <HStack gap={1} wrap="wrap">
          {card.number && <Token size="sm" label={card.number} />}
          {card.rarity && <Token size="sm" color="purple" label={card.rarity} />}
        </HStack>
        {card.artist && (
          <Text type="supporting" color="secondary">
            {t("image.illustrator", { name: card.artist })}
          </Text>
        )}
        {!imageUrl && (
          <Text type="supporting" color="secondary">{t("image.pending")}</Text>
        )}
      </VStack>
    </div>
  );
}
